// @ts-strict-ignore
import * as React from 'react';
import {
    AnyTimelineEventsFilter,
    TimelineEventsFilterType,
    DateFilter,
    ReleaseStepFilter,
    AutomationFilter,
    VersionFilter,
    AppFilter,
    DestinationFilter,
} from '../../types/timelineEventsFilters';
import { compact, flatMap } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import Form from 'antd/lib/form';
import { updateTimelineEventsFilters } from '../../actions/filters';
import { RootState } from '../../reducers/reducers';
import {
    selectDefaultReleaseStepsForApp,
    selectTimelineEventsFiltersMap,
    selectAppStoreType,
    selectIsWorkflowsCIStepEnabled,
} from '../../selectors/selectors';
import Space from 'antd/lib/space';
import { antGrey } from '../styles/globalStyles';
import Button from 'antd/lib/button';
import Select from 'antd/lib/select';
import { filterOptionLabel } from '../../helpers/formHelpers';
import {
    displayTextForReleaseStepType,
    automationDisplayNameForAutomationId,
    iconForAutomationType,
    displayStringForDestination,
} from '../../helpers/displayHelpers';
import { AppEntity, AppPlatform, AppStoreType, TimelineEventType, ReleaseStepType, AutomationType } from '../../types';
import DatePicker from 'antd/lib/date-picker';
import Icon from '@ant-design/icons';
import { Destination } from '@/types/destinations';
import { useMdmPlatformOverride } from '@/helpers/mdmPlatformOverride';

type TimelineEventAutomationPair = [TimelineEventType | TimelineEventType[], AutomationType];

const timelineEventTypesForAutomation = (timelineEventTypes: TimelineEventAutomationPair[0]): TimelineEventType[] =>
    Array.isArray(timelineEventTypes) ? timelineEventTypes : [timelineEventTypes];

const timelineEventFilterValue = (timelineEventTypes: TimelineEventAutomationPair[0]): string =>
    JSON.stringify(timelineEventTypesForAutomation(timelineEventTypes));

const timelineEventTypesFromFilterValue = (value: string): TimelineEventType[] => JSON.parse(value);

interface TimelineEventsFiltersComponentProps {
    appId: string;
    isVisible: boolean;
    onApplyFilters: (filters: AnyTimelineEventsFilter[]) => void;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    releaseStepType?: ReleaseStepType;
    availableVersions?: string[];
    availableApps?: AppEntity[];
    availableDestinations?: Destination[];
}

export const TimelineEventsFiltersComponent: React.FC<TimelineEventsFiltersComponentProps> = (
    props: TimelineEventsFiltersComponentProps,
) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const { RangePicker } = DatePicker;

    const timelineAutomations: TimelineEventAutomationPair[] = [
        [TimelineEventType.AppStoreVersionAppStoreBuildChanged, AutomationType.AutoSelectLatestBuild],
        [TimelineEventType.AppStoreVersionCreated, AutomationType.AutoCreateAppStoreVersions],
        [TimelineEventType.AutoDownloadArtifacts, AutomationType.AutoDownloadArtifacts],
        [TimelineEventType.AppStoreVersionReleaseNotesUpdated, AutomationType.AutoApplyMetadata],
        [TimelineEventType.ReleaseCreated, AutomationType.AutoCreateRunwayReleases],
        [TimelineEventType.ReleaseCreatedUpcomingRelease, AutomationType.AutoCreateRunwayReleases],
        [TimelineEventType.ReleaseTagged, AutomationType.AutoTagReleases],
        [TimelineEventType.PRMerged, AutomationType.AutoMergeRunwayPRs],
        [TimelineEventType.FeatureAffiliationsApplied, AutomationType.AutoAddMissingFAs],
        [TimelineEventType.FixLabelsApplied, AutomationType.AutoApplyLabelsToFixTickets],
        [TimelineEventType.ReleaseBranchCreated, AutomationType.AutoKickoffRelease],
        [TimelineEventType.BuildWorkflowTriggered, AutomationType.AutoKickoffTriggerWorkflow],
        [TimelineEventType.IssueTrackingReleaseCompleted, AutomationType.AutoCompleteIssueTrackingReleases],
        [TimelineEventType.IssueTrackingTicketsTransitionedToStatus, AutomationType.AutoTransitionIssueTrackingTickets],
        [TimelineEventType.ReleaseSubmitted, AutomationType.AutoSubmitRelease],
        [TimelineEventType.ReleaseReleased, AutomationType.AutoRelease],
        [TimelineEventType.AppStoreVersionBetaSubmittedForReview, AutomationType.AutoSubmitBetaBuilds],
        [TimelineEventType.BetaBuildTestingGroupsApplied, AutomationType.AutoAssignBetaBuilds],
        [TimelineEventType.ReleaseVersionBumped, AutomationType.AutoBumpVersion],
        [TimelineEventType.BetaBuildPromotedToTestTrack, AutomationType.AutoPromoteBuildsToTestTrack],
        [
            [TimelineEventType.AppStoreVersionPhasedReleaseUpdated, TimelineEventType.HealthMetricsSkippedForStaleData],
            AutomationType.AutoPromoteStablePhasedReleases,
        ],
        [TimelineEventType.AppStoreVersionPhasedReleaseUpdated, AutomationType.AutoHaltUnstablePhasedReleases],
        [TimelineEventType.CommsChannelCreated, AutomationType.AutoCreateReleaseChannels],
        [TimelineEventType.DefaultReviewAttachmentUpdated, AutomationType.AutoApplyReviewAttachments],
        [TimelineEventType.BetaBuildTestingNotesAutoApplied, AutomationType.AutoApplyBetaBuildNotes],
        [TimelineEventType.CIBuildDiffSummaryGenerated, AutomationType.AutoGenerateCIBuildDiffSummary],
        [TimelineEventType.ReleaseBranchBackmergePerformed, AutomationType.AutoBackmergeReleaseBranches],
        [TimelineEventType.AppStoreUsersSynced, AutomationType.AutoSyncAppStoreUsers],
        [TimelineEventType.ReleaseBranchDeleted, AutomationType.AutoDeleteReleaseBranch],
        [TimelineEventType.FixRequestApprovalStatusUpdated, AutomationType.AutoCherryPickFromWorkingBranch],
        [TimelineEventType.BinaryArtifactAppStoreUploadSuccess, AutomationType.AutoUploadBinaryArtifactToAppStore],
        [TimelineEventType.VCSReleaseAssetsUploaded, AutomationType.AutoUploadReleaseAssets],
        [
            TimelineEventType.DsymArtifactStabilityMonitoringUploadSuccess,
            AutomationType.AutoUploadDsymsToStabilityMonitoring,
        ],
        [TimelineEventType.AppStoreVersionPhasedReleaseUpdated, AutomationType.AutoStagedRollout],
        [TimelineEventType.ProvisioningProfileDevicesSynced, AutomationType.AutoSyncAppleProvisioningProfilesDevices],
        [TimelineEventType.DevicesSynced, AutomationType.AutoSyncAppleDevices],
        [TimelineEventType.ReleaseCreated, AutomationType.AutoPrepareRollbackRelease],
        [TimelineEventType.BuildResignStarted, AutomationType.AutoResignRollbackBuild],
        [TimelineEventType.ReleaseSummarySent, AutomationType.AutoDistributeReleaseSummary],
        [TimelineEventType.CommsGroupsSynced, AutomationType.AutoSyncSlackGroups],
        [TimelineEventType.CommsGroupsSynced, AutomationType.AutoSyncTeamsTags],
        [TimelineEventType.CommsPilotUserGroupSynced, AutomationType.AutoSyncSlackPilotUserGroup],
        [TimelineEventType.TestRunCreated, AutomationType.AutoCreateTestRuns],
        [TimelineEventType.LocalizableStringsCherryPicked, AutomationType.AutoCherryPickLocalizableStrings],
        [TimelineEventType.TranslationsUploaded, AutomationType.AutoSyncTranslations],
        [TimelineEventType.MetadataTranslationsUploaded, AutomationType.AutoSyncMetadataTranslations],
        [TimelineEventType.BuildDetailsPostedToTicket, AutomationType.AutoPostBuildInfoOnPMTickets],
        [TimelineEventType.SyncedVCSMetadata, AutomationType.AutoSyncMetadataFromVCS],
        [TimelineEventType.CommittedVCSMetadata, AutomationType.AutoSyncMetadataFromVCS],
    ];

    const mdmPlatformLabel = useMdmPlatformOverride(props.appId)?.label;
    const currentFiltersMap = useSelector((state: RootState) => selectTimelineEventsFiltersMap(state));
    const releaseSteps = useSelector((state: RootState) => selectDefaultReleaseStepsForApp(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const isWorkflowsCIStepEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsCIStepEnabled(state, props.appId),
    );

    const applyFilters = (values: Record<string, any>) => {
        const filters: AnyTimelineEventsFilter[] = compact(
            flatMap(values, (_, type) => {
                switch (type) {
                    case TimelineEventsFilterType.Date: {
                        if (values.date) {
                            const startDate = values.date[0];
                            const endDate = values.date[1];

                            return {
                                type,
                                date: [startDate, endDate],
                            };
                        }
                        return null;
                    }
                    case TimelineEventsFilterType.ReleaseStep: {
                        if (values.releaseStep) {
                            return values.releaseStep.map((releaseStep) => {
                                return {
                                    type,
                                    releaseStep,
                                };
                            });
                        }
                        return null;
                    }
                    case TimelineEventsFilterType.Automation: {
                        if (values.automation) {
                            return values.automation.flatMap((automation) =>
                                timelineEventTypesFromFilterValue(automation).map((timelineEventType) => ({
                                    type,
                                    automation: timelineEventType,
                                })),
                            );
                        }
                        return null;
                    }
                    case TimelineEventsFilterType.Version: {
                        if (values.version) {
                            return values.version.map((version) => {
                                return {
                                    type,
                                    version,
                                };
                            });
                        }
                        return null;
                    }
                    case TimelineEventsFilterType.App: {
                        if (values.app) {
                            return values.app.map((appId) => {
                                return {
                                    type,
                                    appId,
                                };
                            });
                        }
                        return null;
                    }
                    case TimelineEventsFilterType.Destination: {
                        if (values.destination) {
                            return values.destination.map((destinationStepConfigId) => {
                                return {
                                    type,
                                    destinationStepConfigId,
                                };
                            });
                        }
                        return null;
                    }
                }
                // unsupported filters
                return null;
            }),
        );

        dispatch(updateTimelineEventsFilters({ filters }));

        props.onApplyFilters(filters);
    };

    const clearButtonClicked = () => {
        dispatch(updateTimelineEventsFilters({ filters: [] }));
        form.resetFields();
    };

    React.useEffect(() => {
        if (Object.keys(currentFiltersMap).length === 0) {
            form.resetFields();
        }
    }, [currentFiltersMap]);

    const selectedAutomationEventTypes = new Set(
        (currentFiltersMap[TimelineEventsFilterType.Automation] as AutomationFilter[] | undefined)?.map(
            (filter) => filter.automation,
        ) ?? [],
    );
    const automationInitialValues = [...timelineAutomations]
        .sort(
            ([leftEventTypes], [rightEventTypes]) =>
                timelineEventTypesForAutomation(rightEventTypes).length -
                timelineEventTypesForAutomation(leftEventTypes).length,
        )
        .reduce<string[]>((values, [timelineEventTypes]) => {
            const eventTypes = timelineEventTypesForAutomation(timelineEventTypes);
            if (eventTypes.every((eventType) => selectedAutomationEventTypes.has(eventType))) {
                values.push(timelineEventFilterValue(eventTypes));
                eventTypes.forEach((eventType) => selectedAutomationEventTypes.delete(eventType));
            }
            return values;
        }, []);

    const initialValues: Record<TimelineEventsFilterType, unknown> = {
        date:
            currentFiltersMap[TimelineEventsFilterType.Date] ?
                [
                    (currentFiltersMap[TimelineEventsFilterType.Date] as DateFilter[])[0].date[0],
                    (currentFiltersMap[TimelineEventsFilterType.Date] as DateFilter[])[0].date[1],
                ]
            :   null,
        releaseStep:
            currentFiltersMap[TimelineEventsFilterType.ReleaseStep] ?
                (currentFiltersMap[TimelineEventsFilterType.ReleaseStep] as ReleaseStepFilter[]).map(
                    (filter) => filter.releaseStep,
                )
            :   [],
        automation: automationInitialValues,
        version:
            currentFiltersMap[TimelineEventsFilterType.Version] ?
                (currentFiltersMap[TimelineEventsFilterType.Version] as VersionFilter[]).map((filter) => filter.version)
            :   [],
        app:
            currentFiltersMap[TimelineEventsFilterType.App] ?
                (currentFiltersMap[TimelineEventsFilterType.App] as AppFilter[]).map((filter) => filter.appId)
            :   [],
        destination:
            currentFiltersMap[TimelineEventsFilterType.Destination] ?
                (currentFiltersMap[TimelineEventsFilterType.Destination] as DestinationFilter[]).map(
                    (filter) => filter.destinationStepConfigId,
                )
            :   [],
        // Not a user-facing option in this filter popover: it is set directly by the hotfix request page
        hotfixRequest: null,
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
            <div
                style={{
                    paddingLeft: 16,
                    paddingRight: 16,
                    paddingTop: 18,
                    paddingBottom: 18,
                    maxHeight: 450,
                    overflowY: 'scroll',
                }}
            >
                <Form
                    form={form}
                    initialValues={initialValues}
                    onFinish={applyFilters}
                    preserve={false}
                    layout="vertical"
                    className="filters-form"
                >
                    <Space direction="vertical" size={12}>
                        <Form.Item name="date" label="Event date">
                            <RangePicker />
                        </Form.Item>
                        {!props.releaseStepType && releaseSteps?.length ?
                            <Form.Item name="releaseStep" label="Release steps">
                                <Select
                                    allowClear
                                    showSearch
                                    mode="multiple"
                                    placeholder="Select release steps"
                                    filterOption={filterOptionLabel}
                                    maxTagCount={3}
                                    style={{ maxWidth: 400 }}
                                >
                                    {releaseSteps.map((releaseStepType, index) => {
                                        const releaseStepDisplayName = displayTextForReleaseStepType(
                                            releaseStepType,
                                            props.platform,
                                            props.appStoreType,
                                            isWorkflowsCIStepEnabled,
                                        );
                                        return (
                                            <Select.Option
                                                key={index + 1}
                                                value={releaseStepType}
                                                label={releaseStepDisplayName}
                                            >
                                                {releaseStepDisplayName}
                                            </Select.Option>
                                        );
                                    })}
                                </Select>
                            </Form.Item>
                        :   null}
                        <Form.Item name="automation" label="Automations">
                            <Select
                                allowClear
                                showSearch
                                mode="multiple"
                                placeholder="Select automations"
                                filterOption={filterOptionLabel}
                                maxTagCount={3}
                                style={{ maxWidth: 400 }}
                            >
                                {timelineAutomations.map(([timelineEventTypes, automationType], index) => {
                                    const automationDisplayName = automationDisplayNameForAutomationId(
                                        automationType,
                                        appStoreType,
                                    );
                                    if (automationDisplayName) {
                                        return (
                                            <Select.Option
                                                key={index + 1}
                                                value={timelineEventFilterValue(timelineEventTypes)}
                                                label={automationDisplayName}
                                            >
                                                <Icon
                                                    component={iconForAutomationType(automationType)}
                                                    style={{
                                                        color: antGrey.grey7,
                                                        marginRight: 8,
                                                    }}
                                                />
                                                {automationDisplayName}
                                            </Select.Option>
                                        );
                                    } else {
                                        return <></>;
                                    }
                                })}
                            </Select>
                        </Form.Item>
                        {props.availableVersions?.length > 0 && (
                            <Form.Item name="version" label="Version">
                                <Select
                                    allowClear
                                    showSearch
                                    mode="multiple"
                                    placeholder="Select versions"
                                    filterOption={filterOptionLabel}
                                    maxTagCount={3}
                                    style={{ maxWidth: 400 }}
                                >
                                    {props.availableVersions.map((version, index) => (
                                        <Select.Option key={index} value={version} label={version}>
                                            {version}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                        {props.availableApps?.length > 0 && (
                            <Form.Item name="app" label="App">
                                <Select
                                    allowClear
                                    showSearch
                                    mode="multiple"
                                    placeholder="Select apps"
                                    filterOption={filterOptionLabel}
                                    maxTagCount={3}
                                    style={{ maxWidth: 400 }}
                                >
                                    {props.availableApps.map((app) => (
                                        <Select.Option key={app.id} value={app.id} label={app.appSettings.appName}>
                                            {app.appSettings.appName}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                        {props.availableDestinations?.length > 0 && (
                            <Form.Item name="destination" label="Destination">
                                <Select
                                    allowClear
                                    showSearch
                                    mode="multiple"
                                    placeholder="Select destinations"
                                    filterOption={filterOptionLabel}
                                    maxTagCount={3}
                                    style={{ maxWidth: 400 }}
                                >
                                    {props.availableDestinations.map((destination) => {
                                        const destinationDisplayName = displayStringForDestination(
                                            destination.platform,
                                            destination.appStoreType,
                                            destination.name,
                                            destination.appName,
                                            mdmPlatformLabel,
                                            destination.shouldOmitPlatformFromAppName,
                                        );
                                        return (
                                            <Select.Option
                                                key={destination.stepConfigId}
                                                value={destination.stepConfigId}
                                                label={destinationDisplayName}
                                            >
                                                {destinationDisplayName}
                                            </Select.Option>
                                        );
                                    })}
                                </Select>
                            </Form.Item>
                        )}
                    </Space>
                </Form>
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: 12,
                    backgroundColor: antGrey.grey3,
                }}
            >
                <Button size="small" onClick={clearButtonClicked}>
                    Clear
                </Button>
                <Button size="small" onClick={form.submit} type="primary">
                    Apply
                </Button>
            </div>
        </div>
    );
};
